import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { buildPlatformNav, isNavItemActive } from './platformNav';

/*
 * The platform sidebar, drawn once.
 *
 * Two consumers: `components/Layout`, which wraps it in the app shell, and the
 * marketing homepage, which mounts the real Dashboard behind its Control
 * surface and needs the nav beside it for the same reason a screenshot of a
 * dashboard needs it — without the rail the reader is looking at a widget, not
 * at a product.
 *
 * It is presentational and route-agnostic: it takes the active path rather than
 * calling useLocation, so the marketing copy can pin `/dashboard` as active
 * while the browser is on `/`. It renders `Link`s, which is why the marketing
 * consumer mounts it inside a MemoryRouter — a click there moves a router that
 * is not the page's.
 *
 * The two variants differ only in link emphasis. The desktop rail is the
 * primary surface and carries the glow; the mobile drawer is transient and
 * does not. Keeping that as a prop rather than two components is what stops the
 * item list from being written twice.
 */

const LINK_BASE = 'flex items-center px-4 py-3 text-sm font-medium rounded-lg';

const VARIANTS = {
  desktop: {
    motion: 'transition-all duration-200',
    active: 'bg-primary-600 text-white shadow-lg glow',
  },
  mobile: {
    motion: 'transition-colors',
    active: 'bg-primary-600 text-white',
  },
};

const IDLE = 'text-gray-300 hover:bg-gray-800 hover:text-white';

function NavLink({ item, activePath, variant, onNavigate }) {
  const style = VARIANTS[variant] || VARIANTS.desktop;
  const active = isNavItemActive(activePath, item.href);
  return (
    <Link
      to={item.href}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={`${LINK_BASE} ${style.motion} ${active ? style.active : IDLE}`}
    >
      <item.icon className="mr-3 h-5 w-5" />
      {item.name}
    </Link>
  );
}

function AdminGroup({ items, activePath, open, onToggle, onNavigate }) {
  if (items.length === 0) return null;
  const anyActive = items.some((item) => isNavItemActive(activePath, item.href));
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={`flex w-full items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
          anyActive ? 'text-white' : IDLE
        }`}
      >
        <ShieldCheckIcon className="mr-3 h-5 w-5" />
        <span className="flex-1 text-left">Admin</span>
        <ChevronDownIcon className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-1 space-y-1 pl-4">
          {items.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={onNavigate}
              aria-current={isNavItemActive(activePath, item.href) ? 'page' : undefined}
              className={`flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                isNavItemActive(activePath, item.href) ? 'bg-primary-600 text-white' : IDLE
              }`}
            >
              <item.icon className="mr-3 h-5 w-5" />
              {item.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function PlatformSidebarNav({
  items,
  adminItems = [],
  activePath = '',
  variant = 'desktop',
  onNavigate,
  adminOpen,
  onToggleAdmin,
}) {
  /* Uncontrolled by default so a consumer that does not care about the group's
   * state — the marketing surface — does not have to hold it. */
  const [uncontrolledOpen, setUncontrolledOpen] = useState(true);
  const open = adminOpen === undefined ? uncontrolledOpen : adminOpen;
  const toggle = onToggleAdmin || (() => setUncontrolledOpen((value) => !value));

  return (
    <nav className="flex-1 space-y-1">
      {items.map((item) => (
        <React.Fragment key={item.name}>
          {item.section && (
            <div className="pt-4 pb-1 px-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
              {item.section}
            </div>
          )}
          <NavLink item={item} activePath={activePath} variant={variant} onNavigate={onNavigate} />
          {item.href === '/dashboard' && (
            <AdminGroup
              items={adminItems}
              activePath={activePath}
              open={open}
              onToggle={toggle}
              onNavigate={onNavigate}
            />
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

export default function PlatformSidebar({
  user,
  hostname = '',
  activePath = '',
  variant = 'desktop',
  onNavigate,
  adminOpen,
  onToggleAdmin,
  footer = null,
  className = 'flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-6 py-8',
}) {
  const { items, adminItems } = buildPlatformNav({ user, hostname });

  return (
    <div className={className}>
      <div className="flex items-center mb-8">
        <div className="text-2xl font-bold text-gradient">🐻 Bluebear.ai</div>
      </div>

      <PlatformSidebarNav
        items={items}
        adminItems={adminItems}
        activePath={activePath}
        variant={variant}
        onNavigate={onNavigate}
        adminOpen={adminOpen}
        onToggleAdmin={onToggleAdmin}
      />

      {footer}
    </div>
  );
}
